import { css } from '@lastbrand/web-ui-styled-system/css'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentWrapper } from '@src/ds/blocks/section-content-wrapper'

import { FeatureGridCell } from './feature-grid-cell'
import { FeatureGridMedia } from './feature-grid-media'

import type { FeatureGridCellPosition, FeatureGridDSProps } from './feature-grid-ds.types'

/**
 * DS branch of the legacy `FeatureSectionTwo` atom. Panda port of Quince DS 2.0
 * `Widget-FeatureGrid` (node 7007:50794): a full-bleed petal band with a hero media beside a
 * 2-column grid of feature cells split by a cross divider — media-left on desktop, media-top
 * on mobile.
 *
 * Consumed today by the non-PL PDP feature section (`image-with-text-product-details-section-ds`).
 * The prop API is data-source-agnostic (`imageProps`/`videoProps`/`contents`) so the PL
 * `FEATURE_SHOWCASE` widget can reuse it in a follow-up.
 */

const COLUMNS = 2

/**
 * Where a cell sits in the 2-column grid and which of its sides face a neighbour. A side gets a
 * divider only when a cell actually exists across it, so the cross closes exactly for a full 2×2 and
 * degrades without floating dividers for 1–3 items.
 */
export function getCellPosition(index: number, total: number): FeatureGridCellPosition {
  const column = index % COLUMNS
  return {
    isTopRow: index < COLUMNS,
    isLeftColumn: column === 0,
    hasRightBorder: column < COLUMNS - 1 && index + 1 < total,
    hasBottomBorder: index + COLUMNS < total,
  }
}

// Petal band. Width and the `bp-desktop-cap` background cap come from
// `SectionContentWrapper`; content is centred to the same 1600 design frame.
const bandClass = css({ backgroundColor: 'petal' })
// `alignItems: stretch` (desktop) lets the media column grow to the row's height — which the text
// column dictates (see `gridClass`), so the media fills it rather than the media forcing a square.
const contentClass = css({
  display: 'flex',
  flexDirection: { base: 'column', lg: 'row' },
  alignItems: { base: 'center', lg: 'stretch' },
  justifyContent: { lg: 'center' },
  width: 'full',
  // Deliberately a literal, not `DS_SECTION_BACKGROUND_CAP`: Panda statically extracts this file's
  // `css()` calls, and an imported constant is NOT re-resolved when that constant changes (verified
  // with `panda cssgen` — see STRFRNT-16552 review). Single-sourcing the cap needs a Panda theme
  // token, not an import; until then a greppable literal is the honest form.
  maxWidth: { lg: '1600px' },
  marginInline: { lg: 'auto' },
})

// The text column sizes to its own content (icons + copy + `space-80` padding) and so DICTATES the
// widget height — no fixed aspect ratio and no `overflow: hidden`, so tall content is never clipped
// (STRFRNT-15853). `flex: 1 1 50%` holds the even split when both cells render (equal 50% basis → no
// free space → neither grows) yet lets the grid grow to fill the row if the media cell is absent
// (`FeatureGridMedia` renders null with no video + empty image); `minWidth: 0` matches the Figma
// frame's `min-w-px` so text wraps instead of pushing min-content past 50%.
const gridClass = css({
  display: 'grid',
  gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
  width: { base: 'full' },
  flex: { lg: '1 1 50%' },
  minWidth: { lg: 0 },
  paddingBlock: { base: 'space-64', lg: 'space-80' },
  paddingInline: { base: 'space-16', lg: 'space-80' },
})

export function FeatureGridDS({ imageProps, videoProps, contents }: FeatureGridDSProps) {
  return (
    <SectionContentWrapper as="section" className={bandClass} data-testid={TEST_LOCATORS.PDP_SECTIONS.FEATURE_SHOWCASE}>
      <div className={contentClass}>
        <FeatureGridMedia imageProps={imageProps} videoProps={videoProps} />
        <div className={gridClass}>
          {contents.map((content, index) => (
            <FeatureGridCell key={index} content={content} position={getCellPosition(index, contents.length)} />
          ))}
        </div>
      </div>
    </SectionContentWrapper>
  )
}

export default FeatureGridDS
